import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Users, Mail, Star, Bookmark, Download, LogOut, Search, RefreshCw, X, FileText,
} from "lucide-react";
import { api, getToken, clearToken } from "@/lib/api";
import { IMAGES } from "@/lib/content";
import AnalyticsPanel from "@/components/admin/AnalyticsPanel";

const TAGS = ["Newsletter", "Builders Circle", "Book Interest", "Book Buyer", "Speaking", "Contact", "Customer", "VIP"];
const STATUSES = ["new", "engaged", "customer", "archived"];

function Stat({ icon: Icon, label, value, testId }) {
  return (
    <div className="border border-white/12 p-6" data-testid={testId}>
      <Icon className="text-[#D4AF37]" size={20} />
      <p className="font-display text-4xl text-white mt-4">{value ?? "—"}</p>
      <p className="font-body text-xs tracking-[0.16em] uppercase text-white/45 mt-1">{label}</p>
    </div>
  );
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [tag, setTag] = useState("");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [note, setNote] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, c] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/contacts", { params: { q: q || undefined, tag: tag || undefined } }),
      ]);
      setStats(s.data);
      setContacts(c.data.contacts);
      setTotal(c.data.total);
    } catch (err) {
      if (err.response?.status === 401) {
        clearToken();
        navigate("/admin/login");
      }
    } finally {
      setLoading(false);
    }
  }, [q, tag, navigate]);

  useEffect(() => {
    if (!getToken()) {
      navigate("/admin/login");
      return;
    }
    load();
  }, [load, navigate]);

  const logout = () => {
    clearToken();
    navigate("/admin/login");
  };

  const updateStatus = async (id, status) => {
    await api.patch(`/admin/contacts/${id}`, { status });
    load();
    if (selected?.id === id) setSelected({ ...selected, status });
  };

  const addNote = async () => {
    if (!note.trim() || !selected) return;
    const { data } = await api.post(`/admin/contacts/${selected.id}/notes`, { text: note });
    setSelected({ ...selected, notes: [...(selected.notes || []), data.note] });
    setNote("");
  };

  const exportCsv = async () => {
    const res = await api.get("/admin/contacts/export", {
      params: { tag: tag || undefined },
      responseType: "blob",
    });
    const url = window.URL.createObjectURL(new Blob([res.data]));
    const a = document.createElement("a");
    a.href = url;
    a.download = "e2e_contacts.csv";
    a.click();
  };

  return (
    <div className="min-h-screen bg-black" data-testid="admin-dashboard">
      {/* Top bar */}
      <div className="border-b border-white/10 sticky top-0 bg-black/90 backdrop-blur-xl z-30">
        <div className="max-w-[1280px] mx-auto px-6 h-18 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src={IMAGES.logo} alt="E2E" className="h-9" />
            <nav className="flex items-center gap-1">
              <button onClick={() => navigate("/admin")} className="inline-flex items-center gap-2 px-4 py-2 font-body text-sm text-[#D4AF37]">
                <Users size={15} /> CRM
              </button>
              <button onClick={() => navigate("/admin/articles")} data-testid="nav-to-articles" className="inline-flex items-center gap-2 px-4 py-2 font-body text-sm text-white/60 hover:text-white">
                <FileText size={15} /> Articles
              </button>
            </nav>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={exportCsv}
              data-testid="admin-export-csv"
              className="inline-flex items-center gap-2 border border-white/20 text-white/80 hover:border-[#D4AF37] hover:text-[#D4AF37] px-4 py-2.5 font-body text-sm transition-colors"
            >
              <Download size={15} /> Export CSV
            </button>
            <button
              onClick={logout}
              data-testid="admin-logout"
              className="inline-flex items-center gap-2 text-white/60 hover:text-white px-3 py-2.5 font-body text-sm"
            >
              <LogOut size={15} /> Logout
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-[1280px] mx-auto px-6 py-10">
        <h1 className="font-display text-4xl text-white mb-8">Overview</h1>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
          <Stat icon={Users} label="Total Contacts" value={stats?.total_contacts} testId="stat-total" />
          <Stat icon={Mail} label="Newsletter" value={stats?.newsletter} testId="stat-newsletter" />
          <Stat icon={Bookmark} label="Builders Circle" value={stats?.builders_circle} testId="stat-circle" />
          <Stat icon={Star} label="Unread Messages" value={stats?.unread_messages} testId="stat-unread" />
        </div>

        {/* Analytics: growth, funnel, top sources */}
        <AnalyticsPanel />

        <div className="mt-16 mb-6 flex items-end justify-between">
          <div>
            <p className="font-body text-xs tracking-[0.2em] uppercase text-white/40">Directory</p>
            <h2 className="font-display text-3xl text-white mt-1">All contacts</h2>
          </div>
        </div>

        {/* Controls */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="relative flex-1 max-w-md">
            <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && load()}
              placeholder="Search name or email…"
              data-testid="admin-search"
              className="w-full bg-black border border-white/20 text-white pl-11 pr-4 py-3 font-body focus:outline-none focus:border-[#D4AF37]"
            />
          </div>
          <select
            value={tag}
            onChange={(e) => setTag(e.target.value)}
            data-testid="admin-tag-filter"
            className="bg-black border border-white/20 text-white px-4 py-3 font-body focus:outline-none focus:border-[#D4AF37]"
          >
            <option value="">All tags</option>
            {TAGS.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <button
            onClick={load}
            className="inline-flex items-center gap-2 border border-white/20 text-white/80 hover:border-[#D4AF37] px-4 py-3 font-body text-sm"
          >
            <RefreshCw size={15} /> Apply
          </button>
        </div>

        {/* Table */}
        <div className="border border-white/12 overflow-x-auto">
          <table className="w-full" data-testid="admin-contacts-table">
            <thead>
              <tr className="border-b border-white/10 text-left">
                {["Name", "Email", "Tags", "Status", "Source", "Created", ""].map((h) => (
                  <th key={h} className="font-body text-xs tracking-[0.14em] uppercase text-white/40 px-5 py-4">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="px-5 py-10 text-center text-white/40 font-body">Loading…</td></tr>
              ) : contacts.length === 0 ? (
                <tr><td colSpan={7} className="px-5 py-10 text-center text-white/40 font-body">No contacts yet.</td></tr>
              ) : (
                contacts.map((c) => (
                  <tr key={c.id} className="border-b border-white/6 hover:bg-white/[0.03]" data-testid={`contact-row-${c.id}`}>
                    <td className="px-5 py-4 font-body text-white/90 text-sm">{c.name || "—"}</td>
                    <td className="px-5 py-4 font-body text-white/70 text-sm">{c.email}</td>
                    <td className="px-5 py-4">
                      <div className="flex flex-wrap gap-1">
                        {(c.tags || []).map((t) => (
                          <span key={t} className="text-[10px] tracking-wide uppercase border border-[#D4AF37]/40 text-[#D4AF37] px-2 py-0.5">{t}</span>
                        ))}
                      </div>
                    </td>
                    <td className="px-5 py-4">
                      <select
                        value={c.status}
                        onChange={(e) => updateStatus(c.id, e.target.value)}
                        className="bg-black border border-white/20 text-white/80 text-xs px-2 py-1 focus:outline-none focus:border-[#D4AF37]"
                      >
                        {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </td>
                    <td className="px-5 py-4 font-body text-white/50 text-xs">{c.source_page}</td>
                    <td className="px-5 py-4 font-body text-white/40 text-xs">{(c.created_at || "").slice(0, 10)}</td>
                    <td className="px-5 py-4">
                      <button onClick={() => setSelected(c)} className="text-[#D4AF37] text-xs font-body hover:underline">View</button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <p className="font-body text-xs text-white/40 mt-4">{total} total contacts</p>
      </div>

      {/* Detail drawer */}
      {selected && (
        <div className="fixed inset-0 z-40 flex justify-end" data-testid="contact-drawer">
          <div className="absolute inset-0 bg-black/70" onClick={() => setSelected(null)} />
          <div className="relative w-full max-w-md bg-[#0a0a0a] border-l border-white/12 h-full overflow-y-auto p-8">
            <button onClick={() => setSelected(null)} className="absolute top-6 right-6 text-white/50 hover:text-white">
              <X size={22} />
            </button>
            <h3 className="font-display text-3xl text-white">{selected.name || "Contact"}</h3>
            <p className="font-body text-white/60 mt-1">{selected.email}</p>
            {selected.phone && <p className="font-body text-white/50 text-sm mt-1">{selected.phone}</p>}

            <div className="flex flex-wrap gap-1.5 mt-5">
              {(selected.tags || []).map((t) => (
                <span key={t} className="text-[10px] tracking-wide uppercase border border-[#D4AF37]/40 text-[#D4AF37] px-2 py-0.5">{t}</span>
              ))}
            </div>

            <div className="mt-8">
              <p className="font-body text-xs tracking-[0.2em] uppercase text-white/40 mb-3">Internal Notes</p>
              <div className="space-y-3">
                {(selected.notes || []).length === 0 && <p className="text-white/30 font-body text-sm">No notes yet.</p>}
                {(selected.notes || []).map((n, i) => (
                  <div key={i} className="border border-white/10 p-3">
                    <p className="font-body text-sm text-white/80">{n.text}</p>
                    <p className="font-body text-[10px] text-white/30 mt-1">{n.author} · {(n.created_at || "").slice(0, 10)}</p>
                  </div>
                ))}
              </div>
              <div className="mt-4 flex gap-2">
                <input
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && addNote()}
                  placeholder="Add a note…"
                  data-testid="note-input"
                  className="flex-1 bg-black border border-white/20 text-white px-3 py-2.5 text-sm font-body focus:outline-none focus:border-[#D4AF37]"
                />
                <button onClick={addNote} data-testid="note-add" className="bg-[#D4AF37] text-black px-4 font-body text-sm font-medium">Add</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
